// frontend/src/modules/expenses/pages/PendingApprovalsPage.tsx
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Inbox, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatMoney } from '../../../design-system/format';
import { GlassCard, Avatar } from '../../../design-system/pds';
import { StatusBadge } from '../components/StatusBadge';
import type { Expense } from '../types';

/**
 * The logged-in approver's queue: chain-routed expenses awaiting THIS user's
 * decision at the current level. Server-filtered (pendingApprovalsForUser).
 */
export function PendingApprovalsPage() {
  const navigate = useNavigate();
  const { tenant } = useBranding();
  const tenantCurrency = (tenant as any)?.currency ?? 'USD';
  const { data, loading, error, status, reload } = useApi<{ rows: Expense[] }>('/api/expenses/pending-approvals');
  const rows = data?.rows ?? [];

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Money · Expenses"
        title="My approvals"
        description="Expenses routed to you for sign-off. Multi-level chains show only the steps you own."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        <GlassCard padding="p-0" className="overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-[13px] text-ink/50 dark:text-white/50">Loading…</div>
          ) : status === 403 ? (
            <NoAccessState description="Your role doesn't include expense approvals. Ask a workspace owner if you think you should have access." />
          ) : error ? (
            <ErrorState title="Couldn't load your approvals" description={error} onRetry={reload} />
          ) : rows.length === 0 ? (
            <div className="py-16 px-8 text-center max-w-md mx-auto">
              <div className="mx-auto mb-5 h-16 w-16 rounded-3xl bg-emerald-500/12 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" strokeWidth={1.6} />
              </div>
              <p className="text-[20px] font-semibold text-ink dark:text-white tracking-[-0.015em]">All clear</p>
              <p className="text-[14px] text-ink/60 dark:text-white/60 mt-2">
                Nothing is waiting on your approval right now.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  <tr className="text-[10.5px] uppercase tracking-[0.14em] text-ink/55 dark:text-white/55">
                    <th className="text-left py-3 px-5 font-semibold">Date</th>
                    <th className="text-left py-3 px-4 font-semibold">Employee</th>
                    <th className="text-left py-3 px-4 font-semibold">Category</th>
                    <th className="text-left py-3 px-4 font-semibold">Level</th>
                    <th className="text-right py-3 px-4 font-semibold">Amount</th>
                    <th className="text-right py-3 px-5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e, i) => (
                    <motion.tr
                      key={e.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.015, 0.18) }}
                      onClick={() => navigate(`/dashboard/expenses/${e.id}`)}
                      className="cursor-pointer hover:bg-white/40 dark:hover:bg-white/[0.04] transition-colors border-t border-ink/6 dark:border-white/8"
                    >
                      <td className="py-3.5 px-5 text-[13px] text-ink dark:text-white tabular-nums whitespace-nowrap">
                        {formatDate(e.spent_at)}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Avatar name={e.employee_name} size="sm" />
                          <span className="text-[13px] text-ink dark:text-white truncate max-w-[180px]">{e.employee_name}</span>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 text-[13px] text-ink/75 dark:text-white/75 whitespace-nowrap">{e.category_name}</td>
                      <td className="py-3.5 px-4 text-[13px] text-ink/65 dark:text-white/65 whitespace-nowrap">
                        Level {e.current_approval_level + 1}
                      </td>
                      <td className="py-3.5 px-4 text-right text-[14px] font-semibold tabular-nums font-mono text-ink dark:text-white whitespace-nowrap">
                        {formatMoney(e.amount, e.currency || tenantCurrency)}
                      </td>
                      <td className="py-3.5 px-5 text-right"><StatusBadge status={e.status} /></td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </GlassCard>
        {!loading && !error && rows.length === 0 && (
          <p className="text-[12px] text-ink/40 dark:text-white/40 inline-flex items-center gap-1.5">
            <Inbox className="h-3.5 w-3.5" /> Only chain-routed expenses appear here. Single-step approvals live in the main list.
          </p>
        )}
      </div>
    </div>
  );
}
